import type { OrdersSection } from "@acto/genui-components-schema";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { OrderInvoiceStatus } from "@acto/gql/graphql";
import { useCurrencyContext } from "@acto/taskpane/core/contexts/TenantSettingsContext";
import { formatCurrency, formatNumericDate } from "@acto/taskpane/field-sales-2/utils/format";
import { EntryRows, type Entry } from "./EntryRows";
import { useOrderDrill } from "./useOrderDrill";

const NOT_FULLY_INVOICED = [OrderInvoiceStatus.Open, OrderInvoiceStatus.PartiallyInvoiced] as const;

export function useOrderScopeLabel(section: OrdersSection): string {
  const t = useTranslations("fieldSalesTwo.genuiMeetingPrep.orderCard");
  const { locale } = useLocale();
  return (
    section.scopeLabel ??
    t("scopeRange", {
      start: formatNumericDate(section.dateRange.start, locale),
      end: formatNumericDate(section.dateRange.end, locale),
    })
  );
}

interface OrderScopeEntriesProps {
  section: OrdersSection;
  allOrdersTitle: string;
  productId?: string;
  orderListEyebrow?: string;
}

export function OrderScopeEntries({ section, allOrdersTitle, productId, orderListEyebrow }: OrderScopeEntriesProps) {
  const t = useTranslations("fieldSalesTwo.genuiMeetingPrep.orderCard");
  const { locale } = useLocale();
  const currency = useCurrencyContext();
  const scope = useOrderScopeLabel(section);
  const drill = useOrderDrill();
  const ordersInScope = { kind: "orders", dateRange: section.dateRange, productId, eyebrow: orderListEyebrow } as const;

  const entries: Entry[] = [
    {
      title: allOrdersTitle,
      meta: scope,
      onOpen: () => drill.open({ ...ordersInScope, name: allOrdersTitle }),
    },
  ];
  if (section.openOrderValue) {
    entries.push({
      title: t("openOrders"),
      meta: t("openOrdersMeta", { scope, value: formatCurrency(section.openOrderValue, currency, locale) }),
      onOpen: () => drill.open({ ...ordersInScope, name: t("openOrders"), invoiceStatuses: NOT_FULLY_INVOICED }),
    });
  }
  return (
    <>
      {drill.overlay}
      <EntryRows entries={entries} />
    </>
  );
}
